import {
  ALL_MODULES,
  isPrivilegedRole,
  type ModuleKey,
  type PermissionCode,
  permissionsForMember,
} from "@adpilot/shared";
import { useAuthStore } from "@/stores/auth";

/** Permission codes — single source of truth in @adpilot/shared. */
export type { PermissionCode } from "@adpilot/shared";

/**
 * Permission check hook (ADP-167 main/sub-account model, ADP-180 admin role).
 *
 * - `isMainAccount` — the caller is the workspace owner
 * - `isAdmin` — owner OR backstage-promoted admin (full access)
 * - `hasModule(key)` — module-level grant for plain members (null = all)
 * - `permissions` / `hasPermission` — legacy codes derived from role + modules
 */
export function usePermission() {
  const user = useAuthStore((state) => state.user);
  const isMainAccount = user?.isMainAccount ?? false;
  const modulePermissions = (user?.modulePermissions ?? null) as ModuleKey[] | null;

  // Older cached payloads may lack `role`; fall back to the ADP-167 fields.
  const role = user?.role ?? (isMainAccount ? "owner" : "member");
  const isAdmin = isPrivilegedRole(role);
  const permissions = permissionsForMember(role, modulePermissions) as PermissionCode[];
  const grantedModules: readonly ModuleKey[] = isAdmin
    ? ALL_MODULES
    : (modulePermissions ?? ALL_MODULES);

  const hasPermission = (code: PermissionCode) => permissions.includes(code);
  const hasModule = (key: ModuleKey) => isAdmin || grantedModules.includes(key);

  return {
    role,
    permissions,
    hasPermission,
    isAdmin,
    isMainAccount,
    modulePermissions,
    grantedModules,
    hasModule,
  };
}

export { ALL_MODULES, type ModuleKey, permissionsForMember } from "@adpilot/shared";
